import {
  Avatar,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  useDisclosure,
  Button,
  Portal,
} from '@chakra-ui/react';
import { BiLogIn, BiLogOut } from 'react-icons/bi';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { MdHelpOutline } from 'react-icons/md';
import { useAuth } from 'src/utils/auth';
import { MarketplaceContext } from 'src/contexts/marketplace';
import { useContext, useState } from 'react';
import { MarketplaceAccount } from 'src/api/v1-api';
import { LoginModal } from 'src/components/organisms';
import { srcFromCDN } from 'src/utils/files';
import { FiUser, FiCreditCard } from 'react-icons/fi';
import NextLink from 'next/link';
import useRequest from 'src/utils/hooks/useRequest';
import useSingleToast from 'src/utils/hooks/toast/useSingleToast';

interface AdminRouteUserProfileProps {
  onNavigate?: () => void;
}

const AdminRouteUserProfile = ({ onNavigate }: AdminRouteUserProfileProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user, logout, isAuthenticated, userLoaded } = useAuth();
  const { marketplace } = useContext(MarketplaceContext);
  const { api } = useRequest();
  const showToast = useSingleToast();
  const [isBillingLoading, setIsBillingLoading] = useState(false);

  const handleLogout = async () => {
    const loggedOut = await logout();
    if (loggedOut) {
      window.location.href = '/';
    }
  };

  const handleBillingPortal = async () => {
    if (!marketplace) return;

    setIsBillingLoading(true);
    try {
      const response = await api.marketplace.billing.getPortalUrl(marketplace.id);

      if (response?.url) {
        // Open billing portal in new window
        window.open(response.url, '_blank', 'noopener,noreferrer');
      } else {
        showToast({
          title: 'Unable to access billing portal',
          description: 'Please try again or contact support if the issue persists.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          sentryAlert: {
            error: new Error('Billing portal URL not returned'),
            level: 'error',
            tags: {
              marketplace_id: String(marketplace.id),
              marketplace_name: marketplace.name,
            },
            extras: {
              action: 'billing_portal_access',
              response: response,
            },
          },
        });
      }
    } catch (error) {
      showToast({
        title: 'Failed to open billing portal',
        description: 'Please try again or contact support if the issue persists.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        sentryAlert: {
          error: error as Error,
          level: 'error',
          tags: {
            marketplace_id: String(marketplace.id),
            marketplace_name: marketplace.name,
          },
          extras: {
            action: 'billing_portal_access',
            error: error,
          },
        },
      });
    } finally {
      setIsBillingLoading(false);
    }
  };

  const getProfilePhoto = () => {
    if (!user || !marketplace) return '';
    const marketplaceAccount = user.marketplace_accounts.find(
      (account: MarketplaceAccount) => account.marketplace_id === marketplace.id,
    );
    return marketplaceAccount?.image_url || '';
  };

  const getProfileName = () => {
    if (!user) return '';
    return user.first_name + ' ' + user.last_name;
  };

  const profilePhoto = getProfilePhoto();

  // Show loading state while user data is being fetched
  if (!userLoaded) {
    return null;
  }

  // If user is authenticated, show avatar with dropdown menu
  if (isAuthenticated && user) {
    return (
      <Menu>
        <MenuButton
          as={Button}
          variant="ghost"
          p={0}
          w="full"
          minW="auto"
          h="auto"
          _hover={{ bg: 'transparent' }}
          _active={{ bg: 'transparent' }}
        >
          <Flex alignItems="center" gap={2} w="full">
            <Avatar
              size="sm"
              name={getProfileName()}
              src={profilePhoto ? srcFromCDN(profilePhoto, 32) : undefined}
              bgColor="gray.200"
            />
            <Text fontSize="sm" fontWeight="medium" isTruncated maxW="160px">
              {user.first_name || user.username}
            </Text>
            <BsThreeDotsVertical size={13} style={{ marginLeft: 'auto' }} />
          </Flex>
        </MenuButton>
        <Portal>
          <MenuList w="260px" zIndex={9999}>
            <MenuItem
              as={NextLink}
              href="/account-info"
              icon={<FiUser />}
              fontSize="sm"
              color="black"
              onClick={onNavigate}
            >
              Account Info
            </MenuItem>
            <MenuItem
              icon={<FiCreditCard />}
              onClick={handleBillingPortal}
              fontSize="sm"
              isDisabled={isBillingLoading}
            >
              {isBillingLoading ? 'Loading...' : 'Billing'}
            </MenuItem>
            <MenuItem
              as="a"
              icon={<MdHelpOutline />}
              href="mailto:support@withloma.com"
              target="_blank"
              rel="noopener noreferrer"
              fontSize="sm"
            >
              Contact Support
            </MenuItem>
            <MenuItem icon={<BiLogOut />} onClick={handleLogout} fontSize="sm">
              Log Out
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>
    );
  }

  // If user is not authenticated, show login button
  return (
    <>
      <Flex
        align="center"
        gap={2}
        borderRadius="md"
        bg="transparent"
        cursor="pointer"
        onClick={onOpen}
        _hover={{ bg: 'transparent' }}
        _active={{ bg: 'transparent' }}
        w="full"
      >
        <BiLogIn size={15} />
        <Text fontSize="sm" noOfLines={1}>
          Log In
        </Text>
      </Flex>
      <LoginModal isOpen={isOpen} onClose={onClose} showSignupForm={false} />
    </>
  );
};

export default AdminRouteUserProfile;
